import {
  CompanyContainer,
  CompanyContentContainer,
  CompanyTabsContent,
  CompanyTabsList,
} from "components/company/v3/CompanyContainers";
import { CompanyOperatingSegmentsTabEnum } from "components/company/v3/menu/enums";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import OperatingSegments from "components/stocks/modern/OperatingSegments/OperatingSegments";
import OperatingSegmentsDataProvider from "components/stocks/modern/OperatingSegments/OperatingSegmentsDataProvider";
import { Tabs } from "components/ui/Tabs";
import { Provider } from "jotai";
import { GlobeIcon, PieChartIcon } from "lucide-react";
import { ErrorBoundary } from "react-error-boundary";
import { FormattedMessage, useIntl } from "react-intl";

interface OperatingSegmentsBoxProps {
  tickerSymbol: string;
  tab: CompanyOperatingSegmentsTabEnum;
  onChangeTab: (tab: CompanyOperatingSegmentsTabEnum) => void;
}

export default function OperatingSegmentsBox({ tickerSymbol, tab, onChangeTab }: OperatingSegmentsBoxProps) {
  const intl = useIntl();

  return (
    <CompanyContainer className="xl:h-full xl:min-h-[553px]">
      <CompanyContentContainer className="grow">
        <Tabs
          defaultValue={CompanyOperatingSegmentsTabEnum.BUSINESS}
          value={tab}
          onValueChange={(value) => onChangeTab(value as CompanyOperatingSegmentsTabEnum)}
          className="h-full w-full xl:h-auto"
        >
          <CompanyTabsList
            tabs={[
              {
                value: CompanyOperatingSegmentsTabEnum.BUSINESS,
                label: intl.formatMessage({
                  defaultMessage: "Receitas",
                  description: "Aba do menu superior da empresa",
                  id: "QMMBPJ",
                }),
                icon: <PieChartIcon className="inline-flex" size={16} strokeWidth={2} />,
              },
              {
                value: CompanyOperatingSegmentsTabEnum.GEOGRAPHIC,
                label: intl.formatMessage({
                  defaultMessage: "Países",
                  description: "Aba do menu superior da empresa",
                  id: "d7X3Af",
                }),
                icon: <GlobeIcon className="inline-flex" size={16} strokeWidth={2} />,
              },
            ]}
          />

          <CompanyTabsContent value={CompanyOperatingSegmentsTabEnum.BUSINESS}>
            <OperatingSegmentsDataProvider tickerSymbols={[tickerSymbol]}>
              {({ businessSegments, loading }) => {
                if (loading) {
                  return <LoadingSkeleton count={1} className="h-full w-full" />;
                }

                if (businessSegments) {
                  return (
                    <ErrorBoundary fallbackRender={() => null}>
                      <Provider>
                        <OperatingSegments
                          kind="BUSINESS"
                          tickerSymbols={[tickerSymbol]}
                          financialSegments={businessSegments}
                        />
                      </Provider>
                    </ErrorBoundary>
                  );
                }

                if (!loading) {
                  return (
                    <div className="flex h-full w-full items-center justify-center">
                      <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
                        <div>
                          <SadFaceIcon />
                        </div>

                        <p>
                          <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
                        </p>
                      </div>
                    </div>
                  );
                }

                return null;
              }}
            </OperatingSegmentsDataProvider>
          </CompanyTabsContent>

          <CompanyTabsContent value={CompanyOperatingSegmentsTabEnum.GEOGRAPHIC}>
            <OperatingSegmentsDataProvider tickerSymbols={[tickerSymbol]}>
              {({ geographicSegments, loading }) => {
                if (loading) {
                  return <LoadingSkeleton count={1} className="h-full w-full" />;
                }
                if (geographicSegments) {
                  return (
                    <ErrorBoundary fallbackRender={() => null}>
                      <Provider>
                        <OperatingSegments
                          kind="GEOGRAPHIC"
                          tickerSymbols={[tickerSymbol]}
                          financialSegments={geographicSegments}
                        />
                      </Provider>
                    </ErrorBoundary>
                  );
                }
                if (!loading) {
                  return (
                    <div className="flex h-full w-full items-center justify-center">
                      <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
                        <div>
                          <SadFaceIcon />
                        </div>

                        <p>
                          <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
                        </p>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            </OperatingSegmentsDataProvider>
          </CompanyTabsContent>
        </Tabs>
      </CompanyContentContainer>
    </CompanyContainer>
  );
}
